import { ExpandMore } from '@mui/icons-material';
import {
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  getBankAccountsForNominee,
  getDocumentAvailablesForPaymentRequest,
} from '../../../../../../../../../../lib/api';
import {
  createPaymentRequest,
  createPaymentRequestCash,
  markDocumentsAsPaid,
} from '../../../../../../../../../../lib/api/payments';
import CustomButton from '../../../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../../../lib/designSystem/CircularProgress';
import EnhancedTable from '../../../../../../../../../../lib/designSystem/Table';
import GenerateNominee from '../../../../../../../Financing/Factoring/components/Recieved/components/ShoppingDialog/components/PaymentModule/components/generateNominee';

interface Document {
  id: number;
  businessName: string;
  businessIdentifier: string;
  amount: number;
  financialNodeId: number;
  financialNodeInternalKey: string;
}

const TOGGLE_BUTTON_OPTIONS = [
  { value: 'creditDocuments', label: 'Crédito', ariaLabel: 'Crédito' },
  { value: 'cashDocuments', label: 'Contado', ariaLabel: 'Contado' },
  {
    value: 'receiptFeeDocuments',
    label: 'Boletas de honorarios',
    ariaLabel: 'Boletas de honorarios',
  },
];

const nomineeHeaders = [
  { key: 'bankAccountOriginName', label: 'Banco origen' },
  {
    key: 'bankAccountDestinationName',
    label: 'Banco destino',
  },
  { key: 'bankAccountDestinationNumber', label: 'N° Cuenta' },
  { key: 'bankAccountDestinationAccountType', label: 'Tipo de cuenta' },
  { key: 'bankAccountDestinationHolderName', label: 'Titular' },
  { key: 'bankAccountDestinationHolderIdentifier', label: 'Rut' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'executedAt', label: 'Ejecutada', type: 'date' },
];

interface PayoutDocumentsProps {
  setOpen: (open: boolean) => void;
  setSuccessOpen: (success: string) => void;
  setFailureOpen: (failure: string) => void;
  setUp: () => Promise<void>;
}

interface PaymentRequest {
  businessName: string;
  amount: number;
  documents: Document[];
}

interface BankAccount {
  id: number;
  bankName: string;
  accountNumber: string;
}

interface Payment {
  id: number;
  businessName: string;
  businessIdentifier: string;
  amount: number;
  financialNodeId: number;
  financialNodeInternalKey: string;
}
const PayoutDocuments = (props: PayoutDocumentsProps) => {
  const { setOpen, setSuccessOpen, setFailureOpen, setUp } = props;
  const [loading, setLoading] = useState(false);
  const [openNonNominee, setOpenNonNominee] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [documentsConfirmed, setDocumentsConfirmed] = useState(false);
  const [destinyAccounts, setDestinyAccounts] = useState<BankAccount[]>([]);
  const [originAccounts, setOriginAccounts] = useState<BankAccount[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<PaymentRequest | null>(
    null,
  );
  const [collapse, setCollapse] = useState(false);
  const [step, setStep] = useState(1);
  const [paymentType, setPaymentType] = useState<
    'cashDocuments' | 'creditDocuments' | 'receiptFeeDocuments'
  >('creditDocuments');
  const [documents, setDocuments] = useState<{
    cashDocuments: [];
    creditDocuments: Document[];
    receiptFeeDocuments: Document[];
  }>({
    cashDocuments: [],
    creditDocuments: [],
    receiptFeeDocuments: [],
  });
  const [openBankSelector, setOpenBankSelector] = useState(false);
  const [bankAccountOriginId, setBankAccountOriginId] = useState<string | null>(
    null,
  );
  const dispatch = useDispatch();

  const setUpDocuments = async () => {
    setLoading(true);
    try {
      const documents = await getDocumentAvailablesForPaymentRequest(dispatch);
      setDocuments(documents);
    } catch (error) {
      alert('Error al cargar los documentos');
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUpDocuments();
  }, []);

  const getStakeholderData = () => {
    if (selectedDocuments.length) {
      const document = documents[paymentType].find(
        (invoice) => invoice.id === selectedDocuments[0],
      );
      if (document) {
        return {
          businessName: document.businessName,
          businessIdentifier: document.businessIdentifier,
        };
      }
    }
    return { businessName: '', businessIdentifier: '' };
  };

  const { businessIdentifier, businessName } = selectedDocuments.length
    ? getStakeholderData()
    : {
        businessIdentifier: '',
        businessName: '',
      };

  const configurePaymentRequest = () => {
    const documentsFiltered = documents[paymentType].filter((doc) => {
      return selectedDocuments.includes(doc.id);
    });
    const request = {
      businessName,
      amount: documentsFiltered.reduce(
        (acc: number, doc) => acc + doc.amount,
        0,
      ),
      documents: documentsFiltered,
    };
    setSelectedRequest(request);
  };
  const setUpConfigurePayment = async () => {
    setLoading(true);
    try {
      const accounts = await getBankAccountsForNominee(
        dispatch,
        businessIdentifier,
      );
      if (accounts) {
        setDestinyAccounts(accounts.businessBankAccounts);
        setOriginAccounts(accounts.ownBankAccounts);
        setDocumentsConfirmed(true);
      }
    } catch (error) {
      setFailureOpen('Error al obtener las cuentas');
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await createPaymentRequest(dispatch, {
        payments,
        documentsToPay: selectedDocuments,
      });
      await setUp();
      setSuccessOpen('Pago configurado con éxito');
    } catch (error) {
      setFailureOpen('Error al configurar el pago');
    }
    setLoading(false);
    setOpen(false);
  };

  const handleSubmitCash = async () => {
    setLoading(true);
    try {
      await createPaymentRequestCash(dispatch, {
        documentsToPayIds: selectedDocuments,
        bankAccountOriginId: bankAccountOriginId,
      });
      await setUp();
      setSuccessOpen('Pago configurado con éxito');
    } catch (error) {
      setFailureOpen('Error al configurar el pago');
    }
    setLoading(false);
    setOpen(false);
  };

  const handleMarkAsPaid = async () => {
    setLoading(true);
    try {
      await markDocumentsAsPaid(dispatch, {
        documentsToPay: selectedDocuments,
      });
      await setUp();
      setSuccessOpen('Documentos marcados como pagados');
    } catch (error) {
      setFailureOpen('Error al marcar como pagado');
    }
    setLoading(false);
    setOpenNonNominee(false);
    setOpen(false);
  };

  const stepChecker = async () => {
    switch (step) {
      case 1:
        await setUpConfigurePayment();
        if (paymentType === 'cashDocuments') {
          return setOpenBankSelector(true);
        }
        configurePaymentRequest();
        return setStep(2);
      case 2:
        if (!payments.length) {
          return setStep(3);
        }
        return await handleSubmit();
    }
  };

  const cantSelectRow = (row: Document) => {
    if (paymentType === 'cashDocuments') return false;
    if (businessIdentifier && row.businessIdentifier !== businessIdentifier)
      return true;
    return false;
  };

  const changePaymentType = (value: 'cashDocuments' | 'creditDocuments') => {
    setPaymentType(value);
    setSelectedDocuments([]);
    setPayments([]);
    setDocumentsConfirmed(false);
    setDestinyAccounts([]);
    setOriginAccounts([]);
    setSelectedRequest(null);
  };

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      {step === 1 && (
        <div>
          <HeaderWithActions
            title="Seleccione las documentos a registrar"
            variant="body1"
            type="subHeader"
          />
          <div style={{ marginBottom: 8 }}>
            <CustomToggleButtonGroup
              value={paymentType}
              onChange={(value) =>
                changePaymentType(value as 'cashDocuments' | 'creditDocuments')
              }
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </div>
          <EnhancedTable
            data={documents[paymentType]}
            dataHeaders={[
              { key: 'folio', label: 'Folio' },
              { key: 'businessName', label: 'Emisor' },
              { key: 'businessIdentifier', label: 'Rut' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'issuedDate',
                label: 'Fecha emisión',
                type: 'eight-days-date',
              },
            ]}
            selected={selectedDocuments}
            setSelected={setSelectedDocuments}
            rowsPerPageDefault={5}
            cantSelectRow={cantSelectRow}
            exportToFile="xlsx"
          />
        </div>
      )}
      {step === 2 && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography fontWeight={600} variant="h6">
              Documentos seleccionados
            </Typography>
            <IconButton onClick={() => setCollapse(!collapse)}>
              <ExpandMore />
            </IconButton>
          </div>
          <Collapse in={collapse}>
            <EnhancedTable
              data={documents[paymentType].filter((doc) =>
                selectedDocuments.includes(doc.id),
              )}
              dataHeaders={[
                { key: 'folio', label: 'folio' },
                { key: 'businessName', label: 'Emisor' },
                { key: 'amount', label: 'monto', type: 'money' },
                { key: 'issuedDate', label: 'Fecha emisión', type: 'date' },
              ]}
              rowsPerPageDefault={5}
            />
          </Collapse>
          <Divider />
        </div>
      )}
      {step === 2 && !payments.length ? (
        <div>
          <GenerateNominee
            availableAccounts={destinyAccounts}
            originAccounts={originAccounts}
            selectedRequest={selectedRequest}
            setPayments={setPayments}
            amount={selectedRequest?.amount || 0}
          />
        </div>
      ) : null}
      {step === 2 && payments.length ? (
        <div>
          <Typography variant="body1" margin={'4px'}>
            Seleccione la configuración de pago
          </Typography>
          <EnhancedTable dataHeaders={nomineeHeaders} data={payments} />
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ padding: '0px 10px' }}>
          {step === 2 && (
            <CustomButton
              color="success"
              onClick={() => setOpenNonNominee(true)}
            >
              {' '}
              Sin nómina{' '}
            </CustomButton>
          )}
        </div>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() =>
              documentsConfirmed ? setDocumentsConfirmed(false) : setOpen(false)
            }
          >
            {' '}
            Cancelar{' '}
          </CustomButton>
          <CustomButton color="primary" onClick={() => stepChecker()}>
            {' '}
            {step === 1 ? 'Siguiente' : 'Finalizar'}{' '}
          </CustomButton>
        </DialogActions>
      </div>
      <Dialog open={openNonNominee} onClose={() => setOpenNonNominee(false)}>
        <DialogTitle>Pago sin nómina</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Esto se hace para facturas que ya fueron pagadas sin un pago
            directo. Normalmente utilizado en prestamos con descuentos o ventas
            con descuentos.
          </Typography>
          <Typography variant="body1">
            Estás seguro de querer continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpenNonNominee(false)}
          >
            Cancelar
          </CustomButton>
          <CustomButton color="primary" onClick={() => handleMarkAsPaid()}>
            Marcar como pagado
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openBankSelector}
        onClose={() => setOpenBankSelector(false)}
      >
        <DialogTitle>Registrar pago en efectivo</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paddingBottom={2}>
            Seleccione el banco de origen para registrar el pago con el cual
            realizó el pago.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Banco</InputLabel>
            <Select
              labelId="filter-label"
              fullWidth
              value={bankAccountOriginId}
              onChange={(e) => setBankAccountOriginId(e.target.value)}
              label="Banco"
            >
              {originAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.bankName} ({account.accountNumber})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpenBankSelector(false)}
          >
            Cancelar
          </CustomButton>
          <CustomButton color="primary" onClick={() => handleSubmitCash()}>
            Registrar pago
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PayoutDocuments;
